import { useState, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { selectUserParams } from '../../../../store/slices/userParamsSlice';
import { useUpdateAnnouncementReadStatusMutation } from '../../../../api';
import useDealerAnnouncementData from './hooks/useDealerAnnouncementData';
import AnnouncementDetailDialog from './components/AnnouncementDetailDialog';
import DealerAnnouncementFilters from './components/DealerAnnouncementFilters';

const DealerAnnouncementsPage = () => {
  const { th } = useLanguage();
  const userParams = useSelector(selectUserParams);
  const dealerId = useMemo(
    () => userParams?.dealerId ?? userParams?.DealerId ?? null,
    [userParams]
  );

  const {
    announcements,
    loading,
    totalCount,
    pagination,
    filterFields,
    customFilterTypes,
    customFilterOptions,
    onPageChange,
    handleApplyFilters,
    handleResetFilters,
    handleSearch,
    refetch
  } = useDealerAnnouncementData(dealerId);
  const [updateReadStatus] = useUpdateAnnouncementReadStatusMutation();
  const [dialogVisible, setDialogVisible] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const columnsRef = useRef(null);

  const handleRead = async (announcement) => {
    setSelectedAnnouncement(announcement);
    setDialogVisible(true);

    if (!announcement.isRead) {
      if (!dealerId) {
        console.error('Cannot update read status: DealerId is missing');
        return;
      }

      try {
        await updateReadStatus({
          AnnounctId: announcement.id,
          DealerId: String(dealerId)
        }).unwrap();

        refetch();
      } catch (err) {
        console.error('Read status update failed', err);
      }
    }
  };

  const unreadBodyTemplate = (rowData) => {
  const isRead = rowData?.readInf === 1 || rowData?.ReadInf === 1;
  return (
    <div className="flex justify-content-center">
      {!isRead && (
        <i
          className="pi pi-circle-fill"
          style={{ color: '#22c55e', fontSize: '0.8rem' }}
        />
      )}
    </div>
  );
};

  const actionBodyTemplate = (rowData) => {
    return (
      <Button
        label={th('common.read')}
        icon="pi pi-eye"
        className="p-button-text p-button-sm"
        onClick={() => handleRead(rowData)}
      />
    );
  };

  const dateBodyTemplate = (rowData) => {
    if (!rowData.announcementDate) return '-';
    const date = new Date(rowData.announcementDate);
    return date.toLocaleDateString('tr-TR');
  };

  if (!dealerId) {
    return (
      <div className="card">
        <h1 className="mb-4">{th('announcement.dealerAnnouncementsTitle')}</h1>
        <div className="text-center p-4">
          <p className="text-700 text-xl">{th('announcement.noDealerIdMessage')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex justify-content-between align-items-center mb-4">
        <h1 className="m-0">{th('announcement.dealerAnnouncementsTitle')}</h1>
      </div>

      <div className="mb-2">
        <DealerAnnouncementFilters
          filterFields={filterFields}
          columns={columnsRef.current}
          customFilterTypes={customFilterTypes}
          customFilterOptions={customFilterOptions}
          handleSearch={handleSearch}
          handleApplyFilters={handleApplyFilters}
          handleResetFilters={handleResetFilters}
        />
      </div>

      <DataTable
        value={announcements}
        dataKey="id"
        lazy
        paginator
        first={(pagination.PageNumber - 1) * pagination.PageSize}
        rows={pagination.PageSize}
        totalRecords={totalCount}
        onPage={onPageChange}
        loading={loading}
        showGridlines
        stripedRows
        emptyMessage={th('announcement.emptyMessage')}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate={th('table.paginatorTemplate')}
        rowsPerPageOptions={[10, 25, 50, 100]}
      >
        <Column
          field="typeName"
          header={th('announcement.columnHeaders.type')}
          style={{ width: '20%' }}
        />
        <Column
          field="announcementTypeName"
          header={th('announcement.columnHeaders.announcementType')}
          style={{ width: '20%' }}
        />
        <Column
          field="announcementSubject"
          header={th('announcement.columnHeaders.subject')}
          style={{ width: '40%' }}
        />
        <Column
          field="announcementDate"
          header={th('announcement.columnHeaders.announcementDate')}
          body={dateBodyTemplate}
          style={{ width: '15%' }}
        />
        <Column
          header={th('announcement.columnHeaders.unread')}
          body={unreadBodyTemplate}
          style={{ width: '10%', textAlign: 'center' }}
        />
        <Column
          body={actionBodyTemplate}
          exportable={false}
          style={{ width: '15%', textAlign: 'center' }}
        />
      </DataTable>

      <AnnouncementDetailDialog
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        announcement={selectedAnnouncement}
      />
    </div>
  );
};

export default DealerAnnouncementsPage;
